import React, { useEffect, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";

const STORAGE_KEY = "cabcity_cookie_consent";
const CONSENT_MAX_AGE_MONTHS = 6;

const DEFAULT_CONSENT = {
  necessary: true,
  analytics: false,
  advertising: false,
};

const BUTTON_BASE =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";

function ConsentButton({
  children,
  variant = "default",
  className = "",
  ...props
}) {
  const variantClass =
    variant === "outline"
      ? "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground"
      : "bg-primary text-primary-foreground shadow hover:bg-primary/90";

  return (
    <button
      {...props}
      className={`${BUTTON_BASE} ${variantClass} ${className}`.trim()}
    >
      {children}
    </button>
  );
}

function ConsentSwitch({
  checked,
  onCheckedChange,
  disabled = false,
  ...props
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={Boolean(checked)}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onCheckedChange?.(!checked);
      }}
      className={`inline-flex h-5 w-9 shrink-0 items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? "bg-primary" : "bg-input"
      }`}
      {...props}
    >
      <span
        className={`pointer-events-none block h-4 w-4 rounded-full bg-background shadow-lg ring-0 transition-transform ${
          checked ? "translate-x-4" : "translate-x-0"
        }`}
      />
    </button>
  );
}

function updateGoogleConsent(next) {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function () {
      window.dataLayer.push(arguments);
    };

  window.gtag("consent", "update", {
    ad_storage: next.advertising ? "granted" : "denied",
    ad_user_data: next.advertising ? "granted" : "denied",
    ad_personalization: next.advertising ? "granted" : "denied",
    analytics_storage: next.analytics ? "granted" : "denied",
  });
}

function loadConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    const savedAt = new Date(Number(parsed.savedAt));

    if (Number.isNaN(savedAt.getTime())) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    const expiresAt = new Date(savedAt);
    expiresAt.setMonth(expiresAt.getMonth() + CONSENT_MAX_AGE_MONTHS);

    if (Date.now() >= expiresAt.getTime()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return {
      necessary: true,
      analytics: Boolean(parsed.analytics),
      advertising: Boolean(parsed.advertising),
    };
  } catch {
    return null;
  }
}

export default function CookieConsent() {
  const { isEnglish } = useLanguage();
  const [consent, setConsent] = useState(null);
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const stored = loadConsent();
    const initial = stored || DEFAULT_CONSENT;
    setConsent(stored);
    updateGoogleConsent(initial);

    const handleOpenSettings = () => {
      setOpen(true);
      setShowSettings(true);
    };

    window.addEventListener("cabcity:open-cookie-settings", handleOpenSettings);

    return () => {
      window.removeEventListener("cabcity:open-cookie-settings", handleOpenSettings);
    };
  }, []);

  const saveConsent = (next) => {
    const normalized = {
      necessary: true,
      analytics: Boolean(next.analytics),
      advertising: Boolean(next.advertising),
      savedAt: Date.now(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    updateGoogleConsent(normalized);
    setConsent(normalized);
    setOpen(false);
    setShowSettings(false);

    window.dispatchEvent(
      new CustomEvent("cabcity:consent-changed", {
        detail: normalized,
      }),
    );
  };

  const acceptAll = () => {
    saveConsent({
      ...DEFAULT_CONSENT,
      analytics: true,
      advertising: true,
    });
  };

  const rejectOptional = () => {
    saveConsent(DEFAULT_CONSENT);
  };

  const openPreferences = () => {
    setOpen(true);
    setShowSettings(true);
  };

  if (consent && !open) {
    return (
      <button
        type="button"
        onClick={openPreferences}
        className="fixed bottom-4 left-4 z-[9998] rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-lg transition hover:bg-slate-50"
      >
        {isEnglish ? "Cookie settings" : "Gérer mes cookies"}
      </button>
    );
  }

  if (!open && consent === null) {
    return (
      <div className="fixed inset-x-4 bottom-4 z-[9999] mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl sm:p-6">
        <h2 className="font-heading text-lg font-bold text-navy">
          {isEnglish ? "Cookies & privacy" : "Cookies & confidentialité"}
        </h2>

        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          {isEnglish
            ? "We use necessary cookies to operate the site and, with your consent, measurement and advertising cookies to improve our services and measure our campaigns."
            : "Nous utilisons des cookies nécessaires au fonctionnement du site et, avec votre consentement, des cookies de mesure et de publicité afin d'améliorer nos services et mesurer nos campagnes."}
        </p>

        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <ConsentButton
            type="button"
            variant="outline"
            onClick={rejectOptional}
            className="sm:order-1"
          >
            {isEnglish ? "Reject optional" : "Refuser"}
          </ConsentButton>

          <ConsentButton
            type="button"
            variant="outline"
            onClick={openPreferences}
            className="sm:order-2"
          >
            {isEnglish ? "Customize" : "Personnaliser"}
          </ConsentButton>

          <ConsentButton
            type="button"
            onClick={acceptAll}
            className="sm:order-3"
          >
            {isEnglish ? "Accept all" : "Tout accepter"}
          </ConsentButton>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl sm:p-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-heading text-xl font-bold text-navy">
              {isEnglish ? "Cookie preferences" : "Préférences de cookies"}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              {isEnglish
                ? "Choose which optional categories you allow."
                : "Choisissez les catégories facultatives que vous autorisez."}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-lg px-2 py-1 text-slate-500 hover:bg-slate-100"
            aria-label={isEnglish ? "Close" : "Fermer"}
          >
            ×
          </button>
        </div>

        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 p-4">
            <div>
              <p className="font-semibold text-slate-900">
                {isEnglish ? "Necessary" : "Nécessaires"}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                {isEnglish
                  ? "Required for the website to function."
                  : "Nécessaires au fonctionnement du site."}
              </p>
            </div>
            <ConsentSwitch checked disabled aria-label={isEnglish ? "Necessary cookies always enabled" : "Cookies nécessaires toujours actifs"} />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 p-4">
            <div>
              <p className="font-semibold text-slate-900">
                {isEnglish ? "Analytics" : "Mesure d'audience"}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                {isEnglish
                  ? "Helps us understand how visitors use the site."
                  : "Nous aide à comprendre comment les visiteurs utilisent le site."}
              </p>
            </div>
            <ConsentSwitch
              checked={Boolean(consent?.analytics)}
              onCheckedChange={(checked) =>
                setConsent((current) => ({
                  ...(current || DEFAULT_CONSENT),
                  analytics: checked,
                }))
              }
              aria-label={isEnglish ? "Analytics cookies" : "Cookies de mesure d'audience"}
            />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 p-4">
            <div>
              <p className="font-semibold text-slate-900">
                {isEnglish ? "Advertising" : "Publicité"}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                {isEnglish
                  ? "Used to measure advertising and conversion performance."
                  : "Utilisés pour mesurer les publicités et les conversions."}
              </p>
            </div>
            <ConsentSwitch
              checked={Boolean(consent?.advertising)}
              onCheckedChange={(checked) =>
                setConsent((current) => ({
                  ...(current || DEFAULT_CONSENT),
                  advertising: checked,
                }))
              }
              aria-label={isEnglish ? "Advertising cookies" : "Cookies publicitaires"}
            />
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <ConsentButton type="button" variant="outline" onClick={rejectOptional}>
            {isEnglish ? "Reject optional" : "Refuser"}
          </ConsentButton>
          <ConsentButton
            type="button"
            onClick={() =>
              saveConsent(consent || DEFAULT_CONSENT)
            }
          >
            {isEnglish ? "Save preferences" : "Enregistrer mes choix"}
          </ConsentButton>
        </div>
      </div>
    </div>
  );
}
